
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { useUsdaReports } from "@/hooks/useUsdaReports";
import { useRadiusMatching } from "@/hooks/useRadiusMatching";
import { ReportTabs } from "./reports/ReportTabs";
import { CoordinateTestButton } from "./CoordinateTestButton";
import { LocationNameVerificationButton } from "./LocationNameVerificationButton";
import { StateExtractionDiagnosticButton } from "./StateExtractionDiagnosticButton";
import { BeginningStateExtractionDiagnosticButton } from "./BeginningStateExtractionDiagnosticButton";
import { EnhancedMatchingControls } from "./reports/EnhancedMatchingControls";
import { MatchingSystemOverview } from "./reports/MatchingSystemOverview";
import { EmptyReportsState } from "./reports/EmptyReportsState";
import { RecentReportsSection } from "./reports/RecentReportsSection";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const UsdaReportsSection = () => {
  const { 
    userReports, 
    allReports, 
    loading, 
    error, 
    refreshUserReports,
    fixNullMarketData
  } = useUsdaReports();
  
  const {
    updateProfileCoordinates,
    debugProfileReports,
    loading: radiusLoading,
    stats
  } = useRadiusMatching();
  
  const { toast } = useToast();

  const handleEnhancedRefresh = async () => {
    try {
      console.log('🎯 Starting enhanced radius-based report matching...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user found');
      }

      const { data, error } = await supabase.functions.invoke('fetch-usda-data', {
        body: { 
          matchProfiles: true,
          userId: user.id
        }
      });

      if (error) {
        console.error('Enhanced matching error:', error);
        throw error;
      }

      console.log('Enhanced matching response:', data);
      await refreshUserReports();
      
      toast({
        title: "Enhanced Matching Complete",
        description: data?.message || "Reports matched using enhanced hierarchical system",
      });
      
      console.log('✅ Enhanced radius-based matching completed successfully');
    } catch (error: any) {
      console.error('❌ Enhanced matching failed:', error);
      toast({
        title: "Matching Error",
        description: error.message || "Failed to perform enhanced radius-based matching",
        variant: "destructive",
      });
    }
  };

  const handleUpdateCoordinates = async () => {
    try {
      await updateProfileCoordinates();
      await refreshUserReports();
    } catch (error) {
      console.error('Error updating coordinates:', error);
    }
  };

  const handleDebug = async () => {
    console.log('🐛 Starting debug session...');
    await debugProfileReports();
    
    const { data: reportCount } = await supabase
      .from('usda_reports')
      .select('count(*)')
      .single();
    
    console.log('📊 Total USDA reports in database:', reportCount);
    
    toast({
      title: "Debug Complete",
      description: "Check console for detailed debug information",
    });
  };

  const hasNullData = userReports.some(report => 
    !report.market_types || !report.report_type
  );

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-green-800">USDA Market Reports</h2>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-red-600">USDA Market Reports</h2>
        </CardHeader>
        <CardContent>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertCircle size={16} className="text-red-600 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium mb-1">Error Loading Reports</p>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        <CoordinateTestButton />
        <LocationNameVerificationButton />
        <StateExtractionDiagnosticButton />
        <BeginningStateExtractionDiagnosticButton />
      </div>

      <Card>
        <CardHeader>
          <EnhancedMatchingControls
            userReportsCount={userReports.length}
            radiusLoading={radiusLoading}
            hasNullData={hasNullData}
            onUpdateCoordinates={handleUpdateCoordinates}
            onDebug={handleDebug}
            onFixNullData={fixNullMarketData}
            onEnhancedRefresh={handleEnhancedRefresh}
          />
          
          <MatchingSystemOverview 
            hasNullData={hasNullData} 
            stats={stats} 
          />
        </CardHeader>
        <CardContent>
          {userReports.length === 0 ? (
            <EmptyReportsState
              allReportsCount={allReports.length}
              onDebug={handleDebug}
              onEnhancedRefresh={handleEnhancedRefresh}
            />
          ) : (
            <ReportTabs userReports={userReports} />
          )}
        </CardContent>
      </Card>

      <RecentReportsSection allReports={allReports} />
    </div>
  );
};
